import React from 'react';
import { LuArrowRight, LuBell, LuChartNoAxesCombined, LuFileText, LuSparkles, LuTags } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';

import { Card, CardContent } from '@/components/ui/card';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { useFeatureFlag } from '@/views/auth/useFeatureFlag';
import { titleCase } from 'utils';

import { useNoteTemplatesQuery } from './notetemplate/note-template-queries';
import {
    groupNotificationPreferences,
    useNotificationPreferences,
} from './notifications/notification-preference-queries';
import { useAllKeywordAssociations, useKeywords } from './segmentation/keyword-queries';

const formatCount = (count: number, singular: string, plural = `${singular}s`) =>
    `${count} ${count === 1 ? singular : plural}`;

const formatDate = (value?: string) => {
    if (!value) {
        return undefined;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return undefined;
    }

    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
};

const enabledCount = <T extends { enabled: boolean }>(items: T[]) => items.filter(({ enabled }) => enabled).length;

const IconBadge = ({ children }: { children: React.ReactNode }) => (
    <div className="bg-seafoam-100 text-seafoam-600 flex size-9 shrink-0 items-center justify-center rounded-md">
        {children}
    </div>
);

const ManageLink = () => (
    <span className="text-cocoa-1000 mt-auto flex items-center gap-1 pt-10 text-sm font-[500]">
        Manage <LuArrowRight size={14} />
    </span>
);

const PreferenceCard = ({
    children,
    icon,
    title,
    to,
}: {
    children: React.ReactNode;
    icon: React.ReactNode;
    title: string;
    to?: string;
}) => {
    const content = (
        <CardContent className="flex h-full min-w-0 flex-col p-5">
            <div className="mb-3 flex min-w-0 items-center gap-3">
                <IconBadge>{icon}</IconBadge>
                <h2 className="text-cocoa-1000 font-heading truncate text-lg">{title}</h2>
            </div>
            {children}
        </CardContent>
    );

    return (
        <Card
            className={`border-cocoa-100 min-w-0 bg-white shadow-sm ${
                to ? 'hover:bg-cocoa-0/60 transition-colors' : ''
            }`}
        >
            {to ? (
                <Link
                    className="focus:ring-seafoam-300 block h-full min-w-0 rounded-lg focus:ring-2 focus:outline-none"
                    to={to}
                >
                    {content}
                </Link>
            ) : (
                content
            )}
        </Card>
    );
};

export const PreferencesOverview = () => (
    <Page>
        <PageTopBar />
        <PageTitleRow>
            <div>
                <PageTitle>Preferences</PageTitle>
                <p className="text-cocoa-600 text-sm">Everything personal to you, at a glance.</p>
            </div>
        </PageTitleRow>
        <PageContent>
            <PreferencesOverviewBody />
        </PageContent>
    </Page>
);

const PreferencesOverviewBody = () => {
    const isReportingSettingsEnabled = useFeatureFlag(FeatureFlag.CC_REPORTING);

    const { data: notificationPreferences = [] } = useNotificationPreferences();
    const notificationGroups = groupNotificationPreferences(notificationPreferences);

    const { data: noteTemplates = [] } = useNoteTemplatesQuery();
    const newestTemplate = [...noteTemplates].sort(
        (left, right) => new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
    )[0];
    const noteTemplateLabel = noteTemplates.length === 1 ? 'template' : 'templates';

    const { data: keywords = [] } = useKeywords();
    const { data: keywordAssociations = [] } = useAllKeywordAssociations(keywords);
    const totalKeywordAssociations = keywordAssociations.reduce(
        (total, association) => total + association.personOrgs.length,
        0,
    );

    return (
        <div className="max-w-7xl">
            <div className="grid min-w-0 grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
                <PreferenceCard icon={<LuSparkles />} title="Claire™" to="/preferences/claire">
                    <p className="text-cocoa-600 text-sm leading-6">
                        Personalize Claire&apos;s writing style and manage what she remembers.
                    </p>
                    <ManageLink />
                </PreferenceCard>

                <PreferenceCard icon={<LuBell />} title="Notifications" to="/preferences/notifications">
                    <p className="text-cocoa-600">
                        <span className="font-heading text-cocoa-1000 text-4xl">
                            {enabledCount(notificationPreferences)}
                        </span>
                        <span className="font-heading text-cocoa-300 px-1 text-4xl">/</span>
                        <span className="font-heading text-cocoa-300 text-4xl">{notificationPreferences.length}</span>
                        <span className="ml-2 text-sm"> events on</span>
                    </p>
                    <p className="text-cocoa-600 mt-2 truncate text-sm">
                        {notificationGroups
                            .map(
                                (group) =>
                                    `${group.title} ${enabledCount(group.preferences)}/${group.preferences.length}`,
                            )
                            .join(' - ')}
                    </p>
                    <ManageLink />
                </PreferenceCard>

                {isReportingSettingsEnabled && (
                    <PreferenceCard
                        icon={<LuChartNoAxesCombined />}
                        title="Reporting settings"
                        to="/preferences/reporting-settings"
                    >
                        <p className="text-cocoa-600 text-sm leading-6">
                            Control which reporting features appear in Portal, Hub, and generated reports.
                        </p>
                        <ManageLink />
                    </PreferenceCard>
                )}

                <PreferenceCard icon={<LuFileText />} title="Note templates" to="/preferences/note-templates">
                    <p className="text-cocoa-600">
                        <span className="font-heading text-cocoa-1000 text-4xl">{noteTemplates.length}</span>
                        <span className="ml-3 text-sm"> {noteTemplateLabel}</span>
                    </p>
                    {newestTemplate && (
                        <p className="text-cocoa-600 mt-2 truncate text-sm">
                            Newest: {newestTemplate.subject || titleCase(newestTemplate.type)} - updated{' '}
                            {formatDate(newestTemplate.updatedAt)}
                        </p>
                    )}
                    <ManageLink />
                </PreferenceCard>

                <PreferenceCard icon={<LuTags />} title="Segmentation keywords" to="/preferences/keyword-segmentation">
                    <div className="flex min-w-0 flex-wrap gap-2">
                        {keywords.slice(0, 3).map((keyword) => (
                            <span
                                key={keyword.keywordGUID}
                                className="border-cocoa-100 text-cocoa-700 max-w-full truncate rounded border px-3 py-1 text-xs"
                            >
                                {keyword.label}
                            </span>
                        ))}
                        {keywords.length > 3 && (
                            <span className="border-cocoa-100 text-cocoa-700 rounded border px-3 py-1 text-xs">
                                +{keywords.length - 3} more
                            </span>
                        )}
                    </div>
                    <p className="text-cocoa-600 mt-4 text-sm">
                        {formatCount(totalKeywordAssociations, 'association')} across contacts
                    </p>
                    <ManageLink />
                </PreferenceCard>
            </div>
        </div>
    );
};
